import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type RestockGroup } from '../api';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Filtro,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, contados, soles } from '../formato';
import { mayuscula, useVocabulario } from '../vocabulario';
import { ListaDelMercado } from './ListaDelMercado';
import { ListaPorRotacion } from './ListaPorRotacion';

/**
 * Qué hay que comprar hoy, y con un toque queda pedido.
 *
 * El número —cuánto falta para llegar al máximo— se calculaba desde el primer
 * día y se enseñaba en dos pantallas **como sugerencia de un campo vacío**:
 * quien compraba leía «pide 18 kg» y después tecleaba las veinte líneas a mano
 * en el formulario de órdenes. Esta pantalla es ese mismo cálculo, ya repartido
 * entre los proveedores a los que hay que mandárselo.
 *
 * Se puede tocar cada cantidad antes de firmar. El sistema sabe cuánto cabe en
 * el estante; el local sabe que el jueves viene con fiesta.
 */
export function Reposicion() {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();

  const consulta = useQuery({
    queryKey: ['reposicion'],
    queryFn: almacen.restock,
  });

  /** Lo que se editó a mano, por insumo. Lo demás va como salió calculado. */
  const [ajustes, setAjustes] = useState<Record<string, string>>({});

  /** Lo que se quitó de esta compra. Se quita, no se pone en cero. */
  const [fuera, setFuera] = useState<Record<string, boolean>>({});

  /**
   * «Por lo vendido» es la lista del mercado: lo perecible que se compra cada
   * madrugada no se rige por su mínimo sino por cuánto sale ese día.
   */
  const [modo, setModo] = useState<'minimo' | 'vendido' | 'rotacion'>('minimo');

  const grupos = consulta.data ?? [];
  const pedibles = grupos.filter((g) => g.supplierId !== null);
  const huerfanos = grupos.filter((g) => g.supplierId === null);

  return (
    <>
      <Cabecera
        titulo="Reposición"
        subtitulo="Lo que hay que comprar hoy, agrupado por proveedor" ayuda="Lo que bajó de su mínimo, con cuánto pedir para llegar al máximo, agrupado por proveedor. Cada grupo se convierte en una orden de compra."
        acciones={
          <Boton onClick={() => void consulta.refetch()}>Recalcular</Boton>
        }
      />

      <div className="cuerpo">
        <div className="filtros">
          <Filtro texto="Por mínimo" activo={modo === 'minimo'} onClick={() => setModo('minimo')} />
          <Filtro texto="Por lo vendido" activo={modo === 'vendido'} onClick={() => setModo('vendido')} />
          <Filtro texto="Por rotación" activo={modo === 'rotacion'} onClick={() => setModo('rotacion')} />
        </div>

        {modo === 'vendido' ? (
          <ListaDelMercado />
        ) : modo === 'rotacion' ? (
          <ListaPorRotacion />
        ) : (
        <>
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="la reposición" />
        ) : grupos.length === 0 ? (
          <Vacio
            titulo="No hay nada que reponer"
            mensaje={`Ningún ${vocabulario.insumo} cruzó su punto de pedido. El punto de pedido es el mínimo más el margen de que el proveedor tarde un día de más, así que esto se llena antes de que falte nada.`}
          />
        ) : (
          <>
            {pedibles.map((grupo) => (
              <OrdenPorFirmar
                key={grupo.supplierId ?? ''}
                grupo={grupo}
                ajustes={ajustes}
                fuera={fuera}
                onAjustar={(supplyId, valor) =>
                  setAjustes((previo) => ({ ...previo, [supplyId]: valor }))
                }
                onQuitar={(supplyId) =>
                  setFuera((previo) => ({
                    ...previo,
                    [supplyId]: !previo[supplyId],
                  }))
                }
                onFirmada={() => void clientes.invalidateQueries()}
              />
            ))}

            {huerfanos.map((grupo) => (
              <Tarjeta key="sin-proveedor">
                <h2 className="tarjeta__titulo">
                  Sin proveedor asignado{' '}
                  <span className="mono apagado">({grupo.lines.length})</span>
                </h2>
                <p className="nota">
                  {/*
                   * No se esconden: son insumos que hay que reponer y nadie
                   * sabe a quién comprárselos. Enseñarlos es lo que hace que
                   * alguien les asigne proveedor; ocultarlos los deja faltando
                   * en silencio.
                   */}
                  Hay que reponerlos y no tienen a quién pedírselos. Se les
                  asigna un proveedor en su ficha, en {mayuscula(vocabulario.insumos)}, y entran en la
                  compra de la próxima vez.
                </p>
                <ul className="lista">
                  {grupo.lines.map((line) => (
                    <li key={line.supplyId} className="lista__fila">
                      <div className="lista__texto">
                        <strong>{line.name}</strong>
                        <span className="apagado mono">
                          {line.sku} · faltan {cantidad(line.suggested)}{' '}
                          {line.unit}
                        </span>
                      </div>
                      <Pastilla texto="Sin proveedor" tono="aviso" />
                    </li>
                  ))}
                </ul>
              </Tarjeta>
            ))}
          </>
        )}
        </>
        )}
      </div>
    </>
  );
}

function OrdenPorFirmar({
  grupo,
  ajustes,
  fuera,
  onAjustar,
  onQuitar,
  onFirmada,
}: {
  grupo: RestockGroup;
  ajustes: Record<string, string>;
  fuera: Record<string, boolean>;
  onAjustar: (supplyId: string, valor: string) => void;
  onQuitar: (supplyId: string) => void;
  onFirmada: () => void;
}) {
  const vocabulario = useVocabulario();
  const [entrega, setEntrega] = useState(grupo.expectedAt);
  const [hecha, setHecha] = useState<string | null>(null);
  // El mismo en cada intento: un doble toque en «Firmar» no crea dos órdenes.
  const [orderId] = useState(newOperationId);

  const dentro = grupo.lines.filter((line) => !fuera[line.supplyId]);

  const cuanto = (supplyId: string, porDefecto: string) =>
    ajustes[supplyId] ?? porDefecto;

  const total = dentro.reduce((suma, line) => {
    const pedido = Number.parseFloat(cuanto(line.supplyId, line.suggested));
    const costo = Number.parseFloat(line.unitCost);

    return suma + (Number.isFinite(pedido) ? pedido * costo : 0);
  }, 0);

  const firmar = useMutation({
    mutationFn: () =>
      almacen.createPurchaseOrder({
        orderId,
        supplierId: grupo.supplierId!,
        expectedAt: entrega,
        lines: dentro.map((line) => ({
          supplyId: line.supplyId,
          quantity: cuanto(line.supplyId, line.suggested),
          unitCost: Number.parseFloat(line.unitCost),
        })),
      }),
    onSuccess: (orden) => {
      setHecha(orden.number);
      onFirmada();
    },
  });

  if (hecha !== null) {
    return (
      <Tarjeta>
        <div className="tarjeta__cabeza">
          <h2 className="tarjeta__titulo">{grupo.supplier}</h2>
          <Pastilla texto={`Orden ${hecha}`} tono="exito" />
        </div>
        <p className="nota nota--pegada">
          Creada en borrador. Se manda desde Órdenes de compra, que es donde
          está el texto para el proveedor.
        </p>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta>
      <div className="tarjeta__cabeza">
        <h2 className="tarjeta__titulo">{grupo.supplier}</h2>
        <div className="campo campo--en-linea">
          <Rotulo htmlFor={`entrega-${grupo.supplierId}`} ayuda="Cuándo debería llegar si se pide hoy, según los días de entrega del proveedor. Se puede cambiar.">Entrega</Rotulo>
          <input
            id={`entrega-${grupo.supplierId}`}
            type="date"
            value={entrega}
            onChange={(e) => setEntrega(e.target.value)}
          />
        </div>
      </div>

      <p className="nota">
        {/*
         * De dónde sale la fecha: del plazo que tiene guardado el proveedor.
         * Decirlo evita que parezca inventada, y se puede cambiar.
         */}
        Entrega calculada con su plazo de {contados(grupo.leadTimeDays, 'día', 'días')}.
        Las cantidades son lo que falta para llenar el estante; se pueden tocar
        antes de firmar.
      </p>

      <div className="tabla-envoltura">
        <table>
          <thead>
            <tr>
              <th>{mayuscula(vocabulario.insumo)}</th>
              <th className="numero"><ConAyuda ayuda="Lo que queda hoy en el almacén.">Hay</ConAyuda></th>
              <th className="numero"><ConAyuda ayuda={`Por debajo de esto el ${vocabulario.insumo} pide reposición.`}>Mínimo</ConAyuda></th>
              <th className="numero"><ConAyuda ayuda="Lo sugerido para llegar al máximo, restando lo que ya está pedido. Se puede cambiar.">Pedir</ConAyuda></th>
              <th className="numero"><ConAyuda ayuda="Lo que costaría, al costo promedio de hoy.">Importe</ConAyuda></th>
              <th />
            </tr>
          </thead>
          <tbody>
            {grupo.lines.map((line) => {
              const quitado = fuera[line.supplyId] === true;
              const pedido = cuanto(line.supplyId, line.suggested);
              const importe =
                Number.parseFloat(pedido) * Number.parseFloat(line.unitCost);

              return (
                <tr key={line.supplyId} className={quitado ? 'fila--fuera' : ''}>
                  <td>
                    <div className="celda-nombre__texto">
                      <strong>{line.name}</strong>
                      <span className="mono apagado">{line.sku}</span>
                    </div>
                  </td>
                  <td className="numero mono apagado">
                    {cantidad(line.quantity)}
                  </td>
                  <td className="numero mono apagado">
                    {cantidad(line.minimum)}
                  </td>
                  <td className="numero">
                    <input
                      className="celda-input"
                      inputMode="decimal"
                      aria-label={`Cuánto pedir de ${line.name}`}
                      disabled={quitado}
                      value={pedido}
                      onChange={(e) => onAjustar(line.supplyId, e.target.value)}
                    />{' '}
                    <span className="apagado mono">{line.unit}</span>
                  </td>
                  <td className="numero">
                    {Number.isFinite(importe) ? soles(importe) : soles(0)}
                  </td>
                  <td className="numero">
                    <button
                      type="button"
                      className="accion-fila"
                      onClick={() => onQuitar(line.supplyId)}
                    >
                      {quitado ? 'Devolver' : 'Quitar'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {firmar.isError ? <Fallo error={firmar.error} /> : null}

      <div className="reposicion__pie">
        <span className="apagado">
          {contados(dentro.length, vocabulario.insumo, vocabulario.insumos)} · total referencial{' '}
          <strong className="mono">{soles(total)}</strong>
        </span>
        <Boton
          variante="primario"
          onClick={() => firmar.mutate()}
          disabled={dentro.length === 0 || firmar.isPending}
        >
          {firmar.isPending ? 'Creando…' : 'Crear orden de compra'}
        </Boton>
      </div>
    </Tarjeta>
  );
}
